import { useEffect, useMemo, useCallback } from "react";
import { useFlows, Flow, FlowStatus } from "../../../stores/flowsStore";

export interface DashboardStats {
  activeCount: number;
  inactiveCount: number;
  draftCount: number;
}

export interface UseDashboardTabReturn {
  // Data
  activeFlows: Flow[];
  stats: DashboardStats;
  loading: boolean;

  // Utilities
  formatDate: (dateStr?: string) => string;
}

export const statusConfig: Record<FlowStatus, { color: "success" | "warning" | "default"; label: string }> = {
  active: { color: "success", label: "Active" },
  inactive: { color: "default", label: "Inactive" },
  draft: { color: "warning", label: "Draft" },
};

export function useDashboardTab(): UseDashboardTabReturn {
  const { flows, fetchFlows, flowsLoading } = useFlows();

  // Fetch flows on mount
  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // Calculate stats from flows
  const stats = useMemo<DashboardStats>(() => ({
    activeCount: flows.filter((f) => f.status === "active").length,
    inactiveCount: flows.filter((f) => f.status === "inactive").length,
    draftCount: flows.filter((f) => f.status === "draft").length,
  }), [flows]);

  // Get only active flows for the table
  const activeFlows = useMemo(() =>
    flows.filter((f) => f.status === "active"),
    [flows]
  );

  // Date formatting utility
  const formatDate = useCallback((dateStr?: string): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  return {
    activeFlows,
    stats,
    loading: flowsLoading,
    formatDate,
  };
}
